import React, { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { change } from "redux-form";

import { isNone } from "~/common/assert";
import {
  CO_TAG_FULL_3_STEPS_CBCT,
  CO_TAG_TEEN_3_STEPS_CBCT,
  CourseId,
  is3DPROCourse,
  isOneArchIncludedInCourse,
  isPowerCapCourse,
  isPromoCodeAvailableForCourse,
  isRetainersCourse,
  Status,
} from "~/common/courses";
import { getLastCorrection } from "~/common/patient";
import { Arch } from "~/common/prescription";
import { canOrderTestPlastic } from "~/common/user";
import { isCBCTServiceAvailableForCourse, type TCourseInstallment } from "~/slices/course-installment";
import { addInstructions, removeInstructions, selectInstructions } from "~/slices/instructions";
import { selectCorrectionFormValues } from "~/slices/redux-form";
import { selectUser } from "~/slices/user";
import { useAppDispatch, useAppSelector } from "~/store";
import type { TPatient } from "~/types/patient";
import type { SetDirtyFn } from "~/types/patient-new";

import { ChildrenShortTooltip, ChildrenV3Tooltip, CourseRadio, CourseRadioGroup, PowerCapCourseTooltip, ScanUploadCTWarning, shouldShowScanUploadCTWarning } from "../patient_new/patient_new_course";

export function PatientUpdateCourse({
  patient,
  courseInstallment,
  setDirty,
  additionalServices,
}: {
  patient: TPatient;
  courseInstallment: TCourseInstallment;
  setDirty: SetDirtyFn;
  additionalServices?: React.ReactNode;
}) {
  const user = useAppSelector(selectUser);
  const instructions = useAppSelector(selectInstructions);
  const formValues = useAppSelector(selectCorrectionFormValues);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isNone(patient.course.course_id)) {
      selectCourse(patient.course.course_id);
    }

    if (patient.deep_cbct || [CO_TAG_FULL_3_STEPS_CBCT, CO_TAG_TEEN_3_STEPS_CBCT].includes(patient.course.course_option_tag)) {
      dispatch(addInstructions({ deep_cbct: true }));
    }

    dispatch(addInstructions({ condition: patient.diagnosis }));
    const lastCorrection = getLastCorrection(patient);

    if (canOrderTestPlastic(user) && lastCorrection.test_plastic != null) {
      dispatch(addInstructions({ test_plastic: lastCorrection.test_plastic }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, patient, user]);

  function selectCourse(course_id: CourseId) {
    dispatch(addInstructions({ course_id }));
    setDirty("course_id", course_id);

    if (instructions.course_id != course_id && instructions.payment_option_id && !(instructions.payment_option_id in courseInstallment[course_id]) && patient.can_edit_payment_option) {
      dispatch(addInstructions({ payment_option_id: null }));
    }

    if (formValues?.arch == Arch.BOTH && isOneArchIncludedInCourse(course_id)) {
      dispatch(change("correction", "arch", null));
    }

    if (!isCBCTServiceAvailableForCourse(courseInstallment, course_id)) {
      dispatch(addInstructions({ deep_cbct: false }));
    }

    if (isRetainersCourse(course_id)) {
      dispatch(addInstructions({ sber_credit: false }));
    }

    if (!isPromoCodeAvailableForCourse(course_id)) {
      dispatch(removeInstructions({ promo_code: undefined }));
    }
  }

  const { course_id = null, deep_cbct } = instructions;

  return (
    <div>
      <h3 id="instructions-block-label" className="block" style={{ fontWeight: "900" }}>
        <FormattedMessage id="BLOCKHEAD_INSTRUCTIONS" />
      </h3>

      <PatientUpdateCourseOptions
        patient={patient}
        selectedCourse={course_id}
        courseInstallment={courseInstallment}
        onCourseChange={selectCourse}
        additionalServices={additionalServices}
      />

      {/*
       * If you use AnimatePresence here, then if you switch from course that displays this warning to
       * retainers, then material options have a nasty buggy layout shift on the right of the screen.
       * So i decided to remove AnimatePresence alltogether, feels better for that edge case.
       */}
      {shouldShowScanUploadCTWarning(courseInstallment, course_id, deep_cbct) ? <ScanUploadCTWarning /> : null}
    </div>
  );
}

function PatientUpdateCourseOptions(props: {
  patient: TPatient;
  selectedCourse: number | null;
  courseInstallment: TCourseInstallment;
  onCourseChange(course: CourseId): void;
  additionalServices?: React.ReactNode;
}) {
  const { patient, selectedCourse, courseInstallment, onCourseChange, additionalServices } = props;

  const isNotChildrenOrChildrenV3 = patient.course.course_id != CourseId.CHILDREN || patient.course.course_option_tag == "CHILDREN_V3";

  return (
    <CourseRadioGroup>
      <CourseRadio
        selectedCourse={selectedCourse}
        courseInstallment={courseInstallment}
        onCourseChange={onCourseChange}
        patient={patient}
        course={CourseId.CBCT}
        image={<img src="/img/deepcbct.png" alt="" width={80} height={42} />}
      />
      {selectedCourse === CourseId.CBCT && additionalServices ? <div className="tw-mb-2 tw-ml-6">{additionalServices}</div> : null}
      {patient.status != Status.UNFILLED_CASE && is3DPROCourse(patient.course.course_id) ? (
        <CourseRadio
          selectedCourse={selectedCourse}
          courseInstallment={courseInstallment}
          onCourseChange={onCourseChange}
          patient={patient}
          course={CourseId.PRO}
          image={<img src="/img/pro_smile_button.svg" alt="" width={105} />}
        />
      ) : null}
      <CourseRadio selectedCourse={selectedCourse} courseInstallment={courseInstallment} onCourseChange={onCourseChange} patient={patient} course={CourseId.EXPERT} />
      {selectedCourse === CourseId.EXPERT && additionalServices ? <div className="tw-mb-2 tw-ml-6">{additionalServices}</div> : null}
      <CourseRadio selectedCourse={selectedCourse} courseInstallment={courseInstallment} onCourseChange={onCourseChange} patient={patient} course={CourseId.TRANSPARENT} />
      {selectedCourse === CourseId.TRANSPARENT && additionalServices ? <div className="tw-mb-2 tw-ml-6">{additionalServices}</div> : null}
      <CourseRadio selectedCourse={selectedCourse} courseInstallment={courseInstallment} onCourseChange={onCourseChange} patient={patient} isNew course={CourseId.LIMITLESS} />
      {selectedCourse === CourseId.LIMITLESS && additionalServices ? <div className="tw-mb-2 tw-ml-6">{additionalServices}</div> : null}
      <CourseRadio selectedCourse={selectedCourse} courseInstallment={courseInstallment} onCourseChange={onCourseChange} patient={patient} course={CourseId.FULL} />
      {selectedCourse === CourseId.FULL && additionalServices ? <div className="tw-mb-2 tw-ml-6">{additionalServices}</div> : null}
      {patient.can_set_teen_course ? (
        <>
          <CourseRadio selectedCourse={selectedCourse} courseInstallment={courseInstallment} onCourseChange={onCourseChange} patient={patient} course={CourseId.TEEN} />
          {selectedCourse === CourseId.TEEN && additionalServices ? <div className="tw-mb-2 tw-ml-6">{additionalServices}</div> : null}
        </>
      ) : null}
      <CourseRadio selectedCourse={selectedCourse} courseInstallment={courseInstallment} onCourseChange={onCourseChange} patient={patient} course={CourseId.SHORT} />
      {selectedCourse === CourseId.SHORT && additionalServices ? <div className="tw-mb-2 tw-ml-6">{additionalServices}</div> : null}
      <CourseRadio selectedCourse={selectedCourse} courseInstallment={courseInstallment} onCourseChange={onCourseChange} patient={patient} course={CourseId.SINGLE_ARCH} />
      {selectedCourse === CourseId.SINGLE_ARCH && additionalServices ? <div className="tw-mb-2 tw-ml-6">{additionalServices}</div> : null}
      <CourseRadio selectedCourse={selectedCourse} courseInstallment={courseInstallment} onCourseChange={onCourseChange} patient={patient} course={CourseId.SUPER_SHORT} />
      {selectedCourse === CourseId.SUPER_SHORT && additionalServices ? <div className="tw-mb-2 tw-ml-6">{additionalServices}</div> : null}
      <CourseRadio
        selectedCourse={selectedCourse}
        courseInstallment={courseInstallment}
        onCourseChange={onCourseChange}
        patient={patient}
        course={CourseId.CHILDREN}
        tooltip={isNotChildrenOrChildrenV3 ? <ChildrenV3Tooltip /> : null}
      />
      {selectedCourse === CourseId.CHILDREN && additionalServices ? <div className="tw-mb-2 tw-ml-6">{additionalServices}</div> : null}
      <CourseRadio
        selectedCourse={selectedCourse}
        courseInstallment={courseInstallment}
        onCourseChange={onCourseChange}
        patient={patient}
        course={CourseId.CHILDREN_SHORT}
        tooltip={<ChildrenShortTooltip />}
      />
      {selectedCourse === CourseId.CHILDREN_SHORT && additionalServices ? <div className="tw-mb-2 tw-ml-6">{additionalServices}</div> : null}
      <CourseRadio selectedCourse={selectedCourse} courseInstallment={courseInstallment} onCourseChange={onCourseChange} patient={patient} course={CourseId.PLAN_3D} />
      {selectedCourse === CourseId.PLAN_3D && additionalServices ? <div className="tw-mb-2 tw-ml-6">{additionalServices}</div> : null}
      <CourseRadio selectedCourse={selectedCourse} courseInstallment={courseInstallment} onCourseChange={onCourseChange} patient={patient} course={CourseId.RETAINERS} />
      {patient.status == Status.UNFILLED_CASE || isPowerCapCourse(patient.course.course_id) ? (
        <CourseRadio
          selectedCourse={selectedCourse}
          courseInstallment={courseInstallment}
          onCourseChange={onCourseChange}
          patient={patient}
          course={CourseId.POWER_CAP}
          tooltip={<PowerCapCourseTooltip />}
        />
      ) : null}
      <CourseRadio selectedCourse={selectedCourse} courseInstallment={courseInstallment} onCourseChange={onCourseChange} patient={patient} course={CourseId.ADDITIONAL_ALIGNERS} />
    </CourseRadioGroup>
  );
}
